import React, { useCallback, useEffect, useState } from 'react';
import { Paper, Table as MuiTable, TableContainer } from '@mui/material';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { saveList, setColumns, setColumnsWithoutWidth, setTotalCount } from 'store/table';
import { RootState } from 'store/types';

import axiosInstance from 'services/axiosInstance';

import TableLoader from './components/TableLoader';
import TablePagination from './components/TablePagination';
import TableBody from './components/TableBody';
import TableHead from './components/TableHead';

import { ITableProps } from './types';

import useStyles from './styles';

const Table: React.FC<ITableProps> = ({ resource, columns = [] }) => {
  const [loading, setLoading] = useState<boolean>(true);

  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { page, rowsPerPage, sorting, filtering } = useAppSelector(
    (state: RootState) => state.table,
  );

  const onMount = useCallback(() => {
    setLoading(true);

    axiosInstance
      .request({
        method: 'GET',
        url: resource,
        params: {
          _start: page * rowsPerPage,
          _end: page * rowsPerPage + rowsPerPage,
          _sort: sorting.field ?? null,
          _order: sorting.order ?? null,
          ...filtering,
        },
      })
      .then(({ headers, data }) => {
        dispatch(saveList(data));
        if (headers['x-total-count']) {
          dispatch(setTotalCount(headers['x-total-count']));
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [page, rowsPerPage, sorting.field, sorting.order, filtering]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      onMount();
    }, 300);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [onMount]);

  useEffect(() => {
    if (columns.length) {
      dispatch(setColumnsWithoutWidth(columns.filter(i => !i.width).length));
    } else {
      dispatch(setColumnsWithoutWidth(columns?.length));
    }
  }, [columns]);

  useEffect(() => {
    if (columns.length) {
      dispatch(setColumns(columns));
    }
  }, [columns]);

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <MuiTable className={classes.table}>
        <TableHead />

        {loading ? (
          <div style={{ overflow: 'auto' }}>
            <TableLoader />
          </div>
        ) : (
          <TableBody />
        )}

        <TablePagination />
      </MuiTable>
    </TableContainer>
  );
};

export default Table;
